import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { FirebaseError } from "firebase/app";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "src/contexts/AuthContext";
import { trackEvent } from "src/hooks/useInstrumentation";
import useErrorHandler from "src/hooks/utility/useErrorHandler";
import SubmitButton from "src/shared/components/Button/SubmitButton";
import { isAdminPasswordSignInDisabledMessage } from "../../../shared-utils/adminPasswordSignIn";
import LoginLinkAction from "./LoginLinkAction";
import LoginStepLayout from "./LoginStepLayout";
import { loginFieldSx, loginPrimaryButtonSx } from "./loginStyles";

interface Props {
  /** Prefilled when the account owner typed an email on the first step. */
  initialEmail: string;
  onSignedIn: () => void;
  /** Omitted when code sign-in is unavailable, so we don't offer a dead end. */
  onUseCode?: (email: string) => void;
}

/** Email/password fallback for non-admin accounts. Admin attempts are blocked. */
export default function PasswordStep({
  initialEmail,
  onSignedIn,
  onUseCode,
}: Props): JSX.Element {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminPasswordError, setAdminPasswordError] = useState<string | null>(
    null
  );
  const authCtx = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const handleLogin = async () => {
    try {
      setLoading(true);
      setAdminPasswordError(null);
      const success = !!(await authCtx?.login(
        email.toLowerCase().trim(),
        password.trim()
      ));
      if (success) {
        trackEvent("Login Screen - Password Sign In Completed");
        onSignedIn();
      }
    } catch (e) {
      if (
        e instanceof FirebaseError &&
        e.code === "auth/internal-error" &&
        isAdminPasswordSignInDisabledMessage(e.message)
      ) {
        setAdminPasswordError(
          t(
            "Admin password login is no longer available. Use your Emerge email to receive a sign-in code."
          )
        );
      } else {
        handleError(e);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginStepLayout
      title={t("Log in with your password.")}
      onBack={onUseCode ? () => onUseCode(email) : undefined}
    >
      <Stack
        component="form"
        spacing={{ xs: 3, sm: 3.5 }}
        onSubmit={(e) => {
          e.preventDefault();
          handleLogin();
        }}
      >
        {adminPasswordError && (
          <Alert severity="error">{adminPasswordError}</Alert>
        )}

        <Stack spacing={1.5}>
          <TextField
            autoFocus
            fullWidth
            type="text"
            value={email}
            placeholder={t("Email")}
            inputProps={{
              "aria-label": t("Email"),
              autoComplete: "username",
            }}
            sx={loginFieldSx}
            onChange={(e) => {
              setAdminPasswordError(null);
              setEmail(e.target.value);
            }}
          />
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            value={password}
            placeholder={t("Password")}
            InputProps={{
              inputProps: {
                "aria-label": t("Password"),
                autoComplete: "current-password",
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t("Toggle password visibility")}
                    onClick={() => setShowPassword((opt) => !opt)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={loginFieldSx}
            onChange={(e) => {
              setAdminPasswordError(null);
              setPassword(e.target.value);
            }}
          />
        </Stack>

        <SubmitButton
          fullWidth
          disabled={!email.length || !password.length}
          loading={loading}
          sx={loginPrimaryButtonSx}
        >
          {t("Log in with Password")}
        </SubmitButton>

        <Stack spacing={0.75} alignItems="center">
          {adminPasswordError && onUseCode && (
            <LoginLinkAction onClick={() => onUseCode(email)}>
              {t("Log in with an email code")}
            </LoginLinkAction>
          )}
          {!adminPasswordError && (
            <LoginLinkAction onClick={() => navigate("/forgot-password")}>
              {t("Reset your password")}
            </LoginLinkAction>
          )}
          <LoginLinkAction onClick={() => navigate("/register")}>
            {t("Register for a new account")}
          </LoginLinkAction>
        </Stack>
      </Stack>
    </LoginStepLayout>
  );
}
